import { useQuery } from '@tanstack/react-query';
import { sitesApi } from '../../lib/sites';
import { usersApi } from '../../lib/users';
import { Trash2, Plus } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SiteAdministratorsProps {
  siteId: string;
  onRemove: (userId: string) => void;
  onAdd: (userId: string) => void;
  isRemoving: boolean;
  isAdding: boolean;
}

export default function SiteAdministrators({
  siteId,
  onRemove,
  onAdd,
  isRemoving,
  isAdding,
}: SiteAdministratorsProps) {
  const { data: siteUsers, isLoading: siteUsersLoading } = useQuery({
    queryKey: ['site-users', siteId],
    queryFn: () => sitesApi.getUsers(siteId),
  });

  const { data: allUsers, isLoading: allUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  });

  // Filter to only site_admin users
  const siteAdmins = siteUsers?.filter((user) => user.role === 'site_admin') || [];
  
  // Get all site_admin users not already assigned to this site
  const availableAdmins = allUsers?.filter(
    (user) => user.role === 'site_admin' && !siteAdmins.some((admin) => admin.id === user.id)
  ) || [];

  const handleAddAdmin = (userId: string) => {
    if (userId) {
      onAdd(userId);
    }
  };

  if (siteUsersLoading || allUsersLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Loading administrators...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Site Administrators</CardTitle>
          {availableAdmins.length > 0 && (
            <Select onValueChange={handleAddAdmin} disabled={isAdding}>
              <SelectTrigger className="w-auto h-8 rounded-md px-3 text-xs font-medium bg-primary shadow hover:bg-primary/90 border-0" style={{ color: 'rgb(250, 250, 250)' }}>
                <div className="flex items-center gap-2" style={{ color: 'rgb(250, 250, 250)' }}>
                  <Plus className="h-4 w-4" />
                  <SelectValue placeholder="Add Administrator" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {availableAdmins.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.email}
                    {user.firstName || user.lastName
                      ? ` (${[user.firstName, user.lastName].filter(Boolean).join(' ')})`
                      : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {siteAdmins.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">No administrators assigned to this site</p>
            {availableAdmins.length > 0 && (
              <Select onValueChange={handleAddAdmin} disabled={isAdding}>
                <SelectTrigger className="w-auto h-8 rounded-md px-3 text-xs font-medium bg-primary shadow hover:bg-primary/90 border-0" style={{ color: 'rgb(250, 250, 250)' }}>
                  <div className="flex items-center gap-2" style={{ color: 'rgb(250, 250, 250)' }}>
                    <Plus className="h-4 w-4" />
                    <SelectValue placeholder="Add Administrator" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {availableAdmins.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.email}
                      {user.firstName || user.lastName
                        ? ` (${[user.firstName, user.lastName].filter(Boolean).join(' ')})`
                        : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead className="hidden sm:table-cell">Name</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {siteAdmins.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="truncate" title={user.email}>
                        {user.email}
                      </div>
                      <div className="sm:hidden mt-1 text-xs text-muted-foreground truncate">
                        {user.firstName || user.lastName
                          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                          : 'No name'}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {user.firstName || user.lastName
                        ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(user.id)}
                        disabled={isRemoving}
                        className="text-destructive hover:text-destructive"
                        title="Remove administrator"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">Remove</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
